import { Component } from '@angular/core';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { NoticeEdit } from '../model/noticeEdit';
import { NoticeService } from 'src/app/core/service/notice.service';
import { NGXLogger } from 'ngx-logger';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { SearchHelperService } from 'src/app/core/utility/search-helper.service';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { GroupDescriptor, CompositeFilterDescriptor, filterBy } from '@progress/kendo-data-query';

@Component({
  selector: 'app-notice-list',
  templateUrl: './notice-list.component.html',
  styleUrls: ['./notice-list.component.scss']
})
export class NoticeListComponent
{
  public pageSizes = [5, 10, 25, 50, 100];
  public view: GridViewManager;
  public deleteDialogOpened = false;
  public enableAdvanced = false;
  public count: number;
  public isNew = false;
  public editDataItem: NoticeEdit;
  public selectedNotice: NoticeEdit;
  public filterText = '';
  public maxTextLength = 100;

  /**
   * Constructor
   */
  constructor(
    private noticeService: NoticeService,
    private logger: NGXLogger,
    private messageHandlerService: MessageHandlerService,
    private appConfigService: AppConfigService,
    private searchHelperService: SearchHelperService
  )
  {
    this.view = new GridViewManager(this.appConfigService.getConfig().pageSize);
    this.loadGridData();
  }

  /**
   * Closes a component
   *
   * @param component Name of component to be closed.
   */
  public close(component: string): void
  {
    this[component + 'Opened'] = false;
  }

  /**
   * Opens a component
   *
   * @param component Name of component to be opened.
   */
  public open(component: string): void
  {
    this[component + 'Opened'] = true;
  }

  /**
   * Button action handler.
   *
   * @param confirm Confirm the action: yes/no
   */
  public deleteAction(confirm: boolean): void
  {
    if (confirm)
    {
      // Proceed with the deletion
      const noticeId = this.selectedNotice.id;
      this.noticeService.deleteNotice(noticeId).subscribe({
        next: () =>
        {
          this.view.data = this.view.data.filter(e => e.id !== noticeId);
          this.view.refreshView();
          this.count = this.view.data.length;
          this.messageHandlerService.displaySuccess('NOTICE-DELETED-SUCCESSFULLY');
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
    // post-deletion cleanup and refresh grid
    this.close('deleteDialog');
    this.selectedNotice = null;
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.view.setState(state);
  }

  /**
   * Group change event handler.
   *
   * @param groups List of GroupDescriptors
   */
  public groupChange(groups: GroupDescriptor[]): void
  {
    this.view.setGroup(groups);
  }

  /**
   * Sets the user to delete, and opens the delete user dialog.
   */
  public deleteHandler(id: number): void
  {
    this.noticeService.getNoticeById(id).subscribe({
      next: (data) =>
      {
        this.selectedNotice = data as NoticeEdit;
        this.open('deleteDialog');
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public addHandler(): void
  {
    this.isNew = true;
    this.editDataItem = new NoticeEdit();
  }

  public editHandler(id: number): void
  {
    this.isNew = false;
    this.noticeService.getNoticeById(id).subscribe({
      next: (data) =>
      {
        this.editDataItem = data;
      },
      error: errorResponse =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public saveChangesHandler(notice: NoticeEdit): void
  {
    this.noticeService.saveNotice(notice, this.isNew, notice.id).subscribe({
      next: (data) =>
      {
        // Must convert the lastModifiedDate into Date object
        data.lastModifiedDate = new Date(data.lastModifiedDate);
        data.startOn = data.startOn !== null ? new Date(data.startOn) : null;
        data.endOn = data.endOn !== null ? new Date(data.endOn) : null;
        data.priorityText = this.getPriorityText(data.priority);
        this.view.addOrUpdateGridRow(data);
        this.count = this.view.data.length;  // Update count

        this.logger.info('saved', notice);

        this.editDataItem = undefined;
        this.messageHandlerService.displaySuccess('NOTICE-SAVED-SUCCESSFULLY');
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public cancelChangesHandler(): void
  {
    this.editDataItem = undefined;
  }

  /**
   * Filtering by search field
   */
  public filterChange(filter: CompositeFilterDescriptor): void
  {
    this.view.state.filter = filter;
    this.view.gridData.data = filterBy(this.view.data, filter);
    this.count = this.view.gridData.data.length;
    this.view.refreshView();
  }

  public searchFieldChange(filterText: string): void
  {
    this.view.state.skip = 0;
    const root = this.searchHelperService.fieldChange(filterText, this.view.state, 'text');
    this.filterText = filterText;
    this.filterChange(root);
  }

  public getPriorityText(priority: string): string
  {
    const notice = this.noticeService.noticePriorities.find(x => x.id === priority);
    return notice && notice.displayName ? notice.displayName : priority;
  }

  private loadGridData(): void
  {
    this.view.startLoading();
    this.noticeService.getNoticesList().subscribe({
      next: (data) =>
      {
        data.forEach(d =>
        {
          d.lastModifiedDate = new Date(d.lastModifiedDate);
          d.startOn = d.startOn !== null ? new Date(d.startOn) : null;
          d.endOn = d.endOn !== null ? new Date(d.endOn) : null;
          d.priorityText = this.getPriorityText(d.priority);
        });
        this.count = data.length;
        this.view.setData(data);
        this.view.setSort([{ field: 'text', dir: 'asc' }]);
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      },
      complete: () =>
      {
        this.view.stopLoading();
      }
    });
  }
}
